/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import {
  DateFilterSchema,
  DbSchema,
  DiscountPromo,
  FreeGoodPromo,
  GroupedPromo,
  GroupPromoListBuyerSchema,
  Nullable,
  PaginationInfoSchema,
  PaginationSchema,
  paramByIDSchema,
  Product,
  ProductWithPromoResponseSchema,
  ReadManySchema,
  RecursiveStatic,
  WarehouseIdSchema,
} from '@diamond/shared/schema';
import { BannerCrudSchema } from '@diamond/shared/schema/banner';
import { Static, Type } from '@sinclair/typebox';

const CustomerTypeCG = Type.Union([Type.Literal('10'), Type.Literal('01')]);

export const GenericFreeGoodPromoSchema = Type.Intersect([
  Type.Pick(GroupedPromo, ['id', 'mechanism']),
  Type.Pick(FreeGoodPromo, [
    'promo_code',
    'valid_from',
    'valid_until',
    'sequential_number_for_free_goods_scale',
    'free_goods_category',
    'quantity_free_goods',
  ]),
  Type.Pick(Product, ['title', 'sales_unit', 'sales_unit_desc']),
  Type.Object({
    product_id: Type.String({ format: 'uuid' }),
    product_item_code: Type.Integer(),
    product_images: Type.Array(Type.String()),
    product_image_thumbnails: Type.Array(Type.String()),
    product_images_medium: Type.Array(Type.String()),
    product_images_large: Type.Array(Type.String()),
    price: Type.Integer(),
  }),
]);

export const SingleFreeGoodPromoSchema = Type.Intersect([
  GenericFreeGoodPromoSchema,
  Type.Pick(FreeGoodPromo, ['minimum_order_quantity', 'minimum_order_amount']),
  Type.Object({
    ref_product_id: Type.String({ format: 'uuid' }), // reference-product's id -> product that make this promo eligible
    is_applies_in_multiples: Type.Boolean(),
  }),
]);

export const BundledFreeGoodPromoSchema = Type.Intersect([
  GenericFreeGoodPromoSchema,
  Type.Pick(FreeGoodPromo, [
    'sum_minimum_order_quantity',
    'sum_minimum_order_amount',
  ]),
]);

export const GenericDiscountPromoSchema = Type.Intersect([
  Type.Pick(GroupedPromo, ['id', 'mechanism']),
  Type.Pick(DiscountPromo, [
    'promo_code',
    'valid_from',
    'valid_until',
    'is_discount',
    'deduction_amount',
    'calculation_number',
  ]),
]);

export const SingleDiscountPromoSchema = Type.Intersect([
  GenericDiscountPromoSchema,
  Type.Pick(DiscountPromo, ['minimum_order_quantity', 'minimum_order_amount']),
  Type.Object({
    is_applies_in_multiples: Type.Boolean(),
    price_after_discount: Nullable(Type.Number()),
  }),
]);

export const BundledDiscountPromoSchema = Type.Intersect([
  GenericDiscountPromoSchema,
  Type.Pick(DiscountPromo, [
    'sum_minimum_order_quantity',
    'sum_minimum_order_amount',
  ]),
]);

export const GroupedPromoFreeGoodItemSchema = Type.Object({
  product: Type.Pick(Product, [
    'id',
    'title',
    'item_code',
    'sales_unit',
    'sales_unit_desc',
    'brand_name',
    'brand_desc',
  ]),
  product_free_good: Type.Intersect([
    Type.Pick(Product, [
      'id',
      'title',
      'item_code',
      'sales_unit',
      'sales_unit_desc',
    ]),
    Type.Pick(FreeGoodPromo, [
      'promo_code',
      'product_brand_name',
      'quantity_free_goods',
      'minimum_order_quantity',
      'minimum_order_amount',
      'sum_minimum_order_quantity',
      'sum_minimum_order_amount',
      'free_goods_product_item_code',
      'free_goods_category',
      'sequential_number_for_free_goods_scale',
    ]),
  ]),
});

export type GroupedPromoFreeGoodItem = Static<
  typeof GroupedPromoFreeGoodItemSchema
>;

export const CompiledProductDiscounts = Type.Array(
  Type.Object({
    product_id: Type.String({ format: 'uuid' }),
    is_discount: Type.Boolean(),
    deduction_amount: Type.Integer(),
    price_after_discount: Nullable(Type.Number()),
  })
);

export const GroupedPromoDiscountItemSchema = Type.Object({
  product: Type.Pick(Product, [
    'id',
    'title',
    'item_code',
    'sales_unit',
    'sales_unit_desc',
    'brand_name',
    'brand_desc',
  ]),
  product_discount: Type.Intersect([
    Type.Pick(DiscountPromo, [
      'promo_code',
      'minimum_order_quantity',
      'minimum_order_amount',
      'sum_minimum_order_quantity',
      'sum_minimum_order_amount',
      'calculation_number',
      'deduction_amount',
      'is_discount',
      'prorate',
    ]),
  ]),
});

export type GroupedPromoDiscountItem = Static<
  typeof GroupedPromoDiscountItemSchema
>;

export const GroupedPromoItemSchema = Type.Union([
  GroupedPromoFreeGoodItemSchema,
  GroupedPromoDiscountItemSchema,
]);

export type GroupedPromoItem = Static<typeof GroupedPromoItemSchema>;

export type CompiledDiscount = {
  productID: string;
  deductionAmount: number;
  priceAfterDiscount: null | number;
};

export const PromoCrudSchema = {
  path: '/promo',
  uploadBanner: BannerCrudSchema.uploadBanner,
  getManyPromo: {
    path: '',
    query: Type.Intersect([
      Type.Pick(ReadManySchema, ['search_query', 'page', 'size']),
      Type.Object({
        customer_type: Type.Optional(CustomerTypeCG),
        status: Type.Optional(
          Type.Union([
            Type.Literal('active'),
            Type.Literal('inactive'),
            Type.Literal('not_yet_active'),
          ])
        ),
        promo_type_by_shipto: Type.Optional(
          Type.Union([Type.Literal('general'), Type.Literal('specific')])
        ),
        mechanism: Type.Optional(
          Type.Union([
            Type.Null(),
            Type.Array(
              Type.Union([
                Type.Literal('discount_percentage'),
                Type.Literal('discount_nominal'),
                Type.Literal('free_goods'),
              ])
            ),
          ])
        ),
      }),
      Type.Optional(DateFilterSchema),
    ]),
    response: Type.Object({
      data: Type.Array(
        Type.Intersect([
          DbSchema['grouped_promos'],
          Type.Object({
            promo_type_by_shipto: Type.Optional(
              Type.Union([Type.Literal('general'), Type.Literal('specific')])
            ),
            specific_shipto_number: Type.Optional(Type.Integer()),
            total_unique_sku: Type.Optional(Type.Integer()),
          }),
        ])
      ),
      pagination: PaginationInfoSchema,
    }),
  },
  getDetailPromo: {
    path: '/buyer/:id/detail',
    params: paramByIDSchema,
    query: WarehouseIdSchema,
    response: Type.Object({
      data: Type.Object({
        promo: Type.Pick(DbSchema['grouped_promos'], [
          'id',
          'mechanism',
          'terms_and_conditions',
          'promo_name',
          'promo_code',
          'promo_type',
          'banner_url',
          'valid_from',
          'valid_until',
        ]),
        products: Type.Array(ProductWithPromoResponseSchema),
      }),
    }),
  },
  updatePromo: {
    path: '/:id',
    params: paramByIDSchema,
    body: Type.Partial(
      Type.Pick(DbSchema['grouped_promos'], [
        'is_enabled',
        'promo_name',
        'banner_url',
        'mechanism',
        'terms_and_conditions',
      ])
    ),
    response: DbSchema['grouped_promos'],
  },
  getManyPromoBuyer: {
    path: '/buyer',
    query: Type.Intersect([
      WarehouseIdSchema,
      PaginationSchema,
      GroupPromoListBuyerSchema,
    ]),
    response: Type.Object({
      data: Type.Array(DbSchema['grouped_promos']),
      pagination: PaginationInfoSchema,
    }),
  },
  reorderPromo: {
    path: '/:id/reorder',
    params: paramByIDSchema,
    body: Type.Object({
      index: Type.Number(),
      customer_type: CustomerTypeCG,
    }),
    response: Type.Null(),
  },
  bringToTopPromo: {
    path: '/:id/bring-to-top',
    params: paramByIDSchema,
    body: Type.Object({
      customer_type: CustomerTypeCG,
    }),
    response: Type.Null(),
  },
  getByIdBackoffice: {
    path: '/:id',
    response: Type.Intersect([
      DbSchema['grouped_promos'],
      Type.Object({
        promo_type_by_shipto: Type.Union([
          Type.Literal('general'),
          Type.Literal('specific'),
        ]),
        specific_shipto_number: Type.Number(),
        product_highlight_total: Type.Number(),
        unique_promo_code_total: Type.Number(),
        promo_code_total: Type.Number(),
        product_higlight_number_type: Type.Object({
          customer_type_01: Type.Optional(Type.Number()),
          customer_type_10: Type.Optional(Type.Number()),
        }),
        terms_promo: Type.Array(
          Type.Object({
            terms: Type.String(),
            promo: Type.String(),
          })
        ),
      }),
    ]),
  },
  getPromoHighlights: {
    path: '/highlights',
    query: WarehouseIdSchema,
    response: Type.Object({
      promo: Type.Optional(DbSchema['grouped_promos']),
      products: Type.Array(ProductWithPromoResponseSchema),
    }),
  },
  getPromoHighlightsGrouped: {
    path: '/highlights-group',
    query: WarehouseIdSchema,
    response: Type.Object({
      promo: Type.Optional(DbSchema['grouped_promos']),
      products: Type.Array(ProductWithPromoResponseSchema),
    }),
  },
  showOnHomepage: {
    path: '/:id/show-on-homepage',
    params: paramByIDSchema,
    body: Type.Pick(DbSchema['grouped_promos'], ['is_display_on_homepage']),
    response: Type.Object({
      promo: DbSchema['grouped_promos'],
      banner: Type.Optional(DbSchema['banner']),
    }),
  },
  getCounterGroupPromo: {
    path: '/counter-group',
    query: WarehouseIdSchema,
    response: Type.Object({
      specific_promo_count: Type.Optional(Type.String()),
      general_promo_count: Type.Optional(Type.String()),
    }),
  },
  getPromoCounterAdmin: {
    path: '/counter',
    query: Type.Intersect([
      Type.Pick(ReadManySchema, ['search_query']),
      Type.Object({
        status: Type.Optional(
          Type.Union([
            Type.Literal('active'),
            Type.Literal('inactive'),
            Type.Literal('not_yet_active'),
          ])
        ),
        promo_type_by_shipto: Type.Optional(
          Type.Union([Type.Literal('general'), Type.Literal('specific')])
        ),
        mechanism: Type.Optional(
          Type.Union([
            Type.Null(),
            Type.Array(
              Type.Union([
                Type.Literal('discount_percentage'),
                Type.Literal('discount_nominal'),
                Type.Literal('free_goods'),
              ])
            ),
          ])
        ),
      }),
      Type.Optional(DateFilterSchema),
    ]),
    response: Type.Object({
      food_service: Type.Number(),
      mitra: Type.Number(),
      combination: Type.Number(),
    }),
  },
  updateProductHighlightNumber: {
    path: '/:id/product-highlight-number',
    params: paramByIDSchema,
    body: Type.Object({
      product_highlight_number:
        DbSchema['grouped_promos']['properties']['product_highlight_number'],
      customer_type: CustomerTypeCG,
    }),
    response: Type.Null(),
  },
  reorderProductHighlight: {
    path: '/:id/reorder-product',
    params: paramByIDSchema,
    body: Type.Pick(DbSchema['grouped_promo_products'], [
      'index',
      'product_item_code',
    ]),
    response: Type.Null(),
  },
  downloadPromoShiptoExcel: {
    path: '/:id/shipto',
    params: paramByIDSchema,
    response: Type.Null(), // Excel file
  },
  getGroupedPromosProductHighlight: {
    path: '/:id/product-highlight',
    params: paramByIDSchema,
    response: Type.Array(
      Type.Intersect([
        Type.Pick(DbSchema['grouped_promo_products'], [
          'index',
          'product_item_code',
        ]),
        Type.Pick(DbSchema['products'], ['title']),
      ])
    ),
  },
  getGroupedPromosTableMechanism: {
    path: '/:id/table-mechanism',
    params: paramByIDSchema,
    query: Type.Intersect([
      PaginationSchema,
      Type.Pick(ReadManySchema, ['search_query']),
    ]),
    response: Type.Object({
      data: Type.Object({
        items: Type.Array(GroupedPromoItemSchema),
      }),
      pagination: PaginationInfoSchema,
    }),
  },
  revertDiscountPromoArchive: {
    path: '/revert-discount-promo-archive',
    body: Type.Object({
      group_ids: Type.Array(Type.String()),
    }),
    response: Type.Null(),
  },
};
export type PromoCrud = RecursiveStatic<typeof PromoCrudSchema>;
